// src/frontend_react/src/pages/AdminDashboard/DataSourceTypeManagementPage.js
import React, { useState, useEffect, useCallback } from 'react';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Table from '../../components/ui/Table/Table';
import Input from '../../components/ui/Input/Input';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Card from '../../components/ui/Card/Card';
import Modal from '../../components/ui/Modal/Modal';
import Icon from '../../components/ui/Icon/Icon';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';
import Badge from '../../components/ui/Badge/Badge';
import Checkbox from '../../components/ui/Checkbox/Checkbox';

import { notify } from '../../services/notificationService';

// API service
import {
  getAdminDataSourceTypes,
  createAdminDataSourceType,
  updateAdminDataSourceType,
  deleteAdminDataSourceType,
} from '../../services/api';

import { FiDatabase, FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';

// Import the SHARED Admin CSS module
import adminPageStyles from './styles.module.css';

function DataSourceTypeManagementPage() {
  const [confirmationInput, setConfirmationInput] = useState('');
  const [dataSourceTypes, setDataSourceTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [currentDST, setCurrentDST] = useState({
    id: null,
    name: '',
    display_name: '',
    description: '',
    expected_filename_pattern: '',
    default_ic_column_name: '',
    default_pii_columns: '', // Stored as comma-separated string in form state
    default_analytical_columns: '', // Stored as comma-separated string in form state
    requires_ic_for_hashing: true,
    perform_column_consistency_check: true,
    category_display_info: '{}',
    common_aliases: '{}',
    column_guidance: '{}',
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dstToDelete, setDstToDelete] = useState(null);

  const fetchDSTs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminDataSourceTypes();
      setDataSourceTypes(
        Array.isArray(data)
          ? data.sort((a, b) => a.name.localeCompare(b.name))
          : [],
      );
    } catch (err) {
      setError(err.message || 'Failed to fetch Data Source Types.');
      setDataSourceTypes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDSTs();
  }, [fetchDSTs]);

  const handleCreateNew = () => {
    setIsEditMode(false);
    setCurrentDST({
      id: null,
      name: '',
      display_name: '',
      description: '',
      expected_filename_pattern: '',
      default_ic_column_name: '',
      default_pii_columns: '',
      default_analytical_columns: '',
      requires_ic_for_hashing: true,
      perform_column_consistency_check: true,
      category_display_info: '{}', // For textarea input
      common_aliases: '{}', // For textarea input
      column_guidance: '{}', // For textarea input
    });
    setFormError(null);
    setIsFormVisible(true);
  };

  const handleEdit = useCallback((dst) => {
    setIsEditMode(true);
    setCurrentDST({
      ...dst,
      default_pii_columns: Array.isArray(dst.default_pii_columns)
        ? dst.default_pii_columns.join(', ')
        : dst.default_pii_columns || '',
      default_analytical_columns: Array.isArray(dst.default_analytical_columns)
        ? dst.default_analytical_columns.join(', ')
        : dst.default_analytical_columns || '',
      requires_ic_for_hashing:
        dst.requires_ic_for_hashing !== undefined
          ? dst.requires_ic_for_hashing
          : true,
      perform_column_consistency_check:
        dst.perform_column_consistency_check !== undefined
          ? dst.perform_column_consistency_check
          : true,
      category_display_info: JSON.stringify(
        dst.category_display_info || {},
        null,
        2,
      ),
      common_aliases: JSON.stringify(dst.common_aliases || {}, null, 2),
      column_guidance: JSON.stringify(dst.column_guidance || {}, null, 2),
    });
    setFormError(null);
    setIsFormVisible(true);
  }, []);

  const openDeleteConfirm = useCallback((dst) => {
    setDstToDelete(dst);
    setConfirmationInput('');
    setShowDeleteConfirm(true);
  }, []);

  const closeDeleteConfirm = () => {
    setDstToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleDelete = async () => {
    if (!dstToDelete) return;
    setIsSubmitting(true);

    setError(null);
    try {
      await deleteAdminDataSourceType(dstToDelete.id);
      notify.success(
        `Data Source Type '${dstToDelete.display_name}' deleted successfully.`,
      ); // Use toast
      fetchDSTs();
    } catch (err) {
      notify.error(
        err.response?.data?.message ||
          err.message ||
          `Failed to delete '${dstToDelete.display_name}'.`,
      ); // Use toast
    } finally {
      setIsSubmitting(false);
      closeDeleteConfirm();
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentDST((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (
      !currentDST.name.trim() ||
      !currentDST.display_name.trim() ||
      !currentDST.expected_filename_pattern.trim()
    ) {
      setFormError(
        'System Name, Display Name, and Expected Filename Pattern are required.',
      );
      return;
    }
    if (!/^[a-z0-9_]+$/.test(currentDST.name.trim())) {
      setFormError(
        'System Name must be lowercase alphanumeric with underscores only.',
      );
      return;
    }
    setFormError(null);
    setIsSubmitting(true);

    const parseJsonField = (fieldName, stringValue) => {
      try {
        return JSON.parse(stringValue);
      } catch (jsonErr) {
        setFormError(`Invalid JSON format for ${fieldName}.`);
        throw jsonErr;
      }
    };

    let payload;
    try {
      payload = {
        name: currentDST.name.trim(),
        display_name: currentDST.display_name.trim(),
        description: currentDST.description.trim(),
        expected_filename_pattern: currentDST.expected_filename_pattern.trim(),
        default_ic_column_name:
          currentDST.default_ic_column_name.trim() || null,
        default_pii_columns: currentDST.default_pii_columns
          ? currentDST.default_pii_columns
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s)
          : [],
        default_analytical_columns: currentDST.default_analytical_columns
          ? currentDST.default_analytical_columns
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s)
          : [],
        requires_ic_for_hashing: currentDST.requires_ic_for_hashing,
        perform_column_consistency_check:
          currentDST.perform_column_consistency_check,
        category_display_info: parseJsonField(
          'Category Display Info',
          currentDST.category_display_info,
        ),
        common_aliases: parseJsonField(
          'Common Aliases',
          currentDST.common_aliases,
        ),
        column_guidance: parseJsonField(
          'Column Guidance',
          currentDST.column_guidance,
        ),
      };
      if (isEditMode && currentDST.id) {
        payload.id = currentDST.id;
      } else if (!isEditMode) {
        delete payload.id;
      }
    } catch (error) {
      setIsSubmitting(false);
      return;
    }

    try {
      if (isEditMode && currentDST.id) {
        await updateAdminDataSourceType(currentDST.id, payload);
      } else {
        await createAdminDataSourceType(payload);
      }
      notify.success(
        `Data Source Type '${payload.display_name}' ${
          isEditMode ? 'updated' : 'created'
        } successfully.`,
      ); // Use toast
      setIsFormVisible(false);
      fetchDSTs();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save Data Source Type.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = React.useMemo(
    () => [
      { Header: 'ID', accessor: 'id', width: 60 },
      {
        Header: 'System Name',
        accessor: 'name',
        width: 150,
        Cell: ({ value }) => (
          <Typography variant="caption" component="code">
            {value}
          </Typography>
        ),
      },
      { Header: 'Display Name', accessor: 'display_name' },
      {
        Header: 'Default IC Col',
        accessor: 'default_ic_column_name',
        width: 180,
        Cell: ({ value }) =>
          value ? (
            <Typography variant="body2">{value}</Typography>
          ) : (
            <Typography variant="caption" color="placeholder">
              N/A
            </Typography>
          ),
      },
      {
        Header: 'Requires IC Hashing',
        accessor: 'requires_ic_for_hashing',
        width: 160,
        thStyle: { textAlign: 'center' }, // Center the header
        Cell: ({ value }) => (
          <Box textAlign="center">
            <Badge variant={value ? 'success' : 'default'} size="sm">
              {value ? 'Yes' : 'No'}
            </Badge>
          </Box>
        ),
      },
      {
        Header: 'Filename Pattern',
        accessor: 'expected_filename_pattern',
        Cell: ({ value }) =>
          value ? (
            <Typography variant="caption" component="code">
              {value}
            </Typography>
          ) : (
            <Typography variant="caption" color="placeholder">
              N/A
            </Typography>
          ),
      },
      {
        Header: 'Actions',
        id: 'actions',
        width: 220,
        Cell: ({ row: dst }) => (
          <HStack spacing="sm">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit(dst)}
              leftIcon={<Icon as={FiEdit} />}
              disabled={isSubmitting || isLoading}
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => openDeleteConfirm(dst)}
              leftIcon={<Icon as={FiTrash2} />}
              disabled={isSubmitting || isLoading}
            >
              Delete
            </Button>
          </HStack>
        ),
      },
    ],
    [isSubmitting, isLoading, handleEdit, openDeleteConfirm],
  );

  if (isLoading && dataSourceTypes.length === 0 && !isFormVisible) {
    return (
      <Card title="Data Source Type Management">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py="xl"
        >
          <Spinner size="lg" />
          <Typography mt="md" color="secondary">
            Loading Data Source Types...
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      title="Data Source Type Management"
      headerActions={
        !isFormVisible && (
          <Button
            variant="primary"
            onClick={handleCreateNew}
            leftIcon={<Icon as={FiPlus} />}
            disabled={isSubmitting || isLoading}
          >
            Create New Type
          </Button>
        )
      }
    >
      <Stack spacing="lg">
        <Typography variant="body1" color="secondary">
          Define and manage the types of data sources the platform can ingest
          (e.g., Insurer Claims, Corporate HR).
        </Typography>

        {error && (
          <Alert variant="error" title="Error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isFormVisible ? (
          <Stack
            spacing="lg"
            pt="lg"
            borderTop="1px solid var(--cipher-color-border-subtle)"
            mt="lg"
          >
            <form onSubmit={handleFormSubmit}>
              <Stack spacing="lg">
                <Typography variant="h4" component="h3" gutterBottom>
                  {isEditMode
                    ? `Edit Type: ${currentDST.display_name}`
                    : 'Create New Data Source Type'}
                </Typography>
                {formError && (
                  <Alert
                    variant="error"
                    title="Form Error"
                    onDismiss={() => setFormError(null)}
                  >
                    {formError}
                  </Alert>
                )}

                <Input
                  label="System Name"
                  id="dstName"
                  name="name"
                  value={currentDST.name}
                  onChange={handleFormChange}
                  required
                  disabled={isSubmitting || isEditMode || isLoading}
                  maxLength={50}
                  hintText={
                    isEditMode
                      ? 'System name cannot be changed.'
                      : 'Short, unique, lowercase_alphanumeric_with_underscores (e.g., insurer_claims).'
                  }
                />
                <Input
                  label="Display Name"
                  id="dstDisplayName"
                  name="display_name"
                  value={currentDST.display_name}
                  onChange={handleFormChange}
                  required
                  disabled={isSubmitting || isLoading}
                  maxLength={100}
                />
                <Input
                  label="Description"
                  id="dstDescription"
                  name="description"
                  value={currentDST.description}
                  onChange={handleFormChange}
                  isTextarea
                  rows={2}
                  disabled={isSubmitting || isLoading}
                />
                <Input
                  label="Expected Filename Pattern"
                  id="dstFilenamePattern"
                  name="expected_filename_pattern"
                  value={currentDST.expected_filename_pattern}
                  onChange={handleFormChange}
                  required
                  disabled={isSubmitting || isLoading}
                  maxLength={255}
                  hintText="A substring to help identify files of this type (e.g., _claims_data_monthly_)."
                />
                <Input
                  label="Default Identifier Column (IC) Name"
                  id="dstIcColumn"
                  name="default_ic_column_name"
                  value={currentDST.default_ic_column_name}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  maxLength={100}
                  hintText="The typical column name containing the IC/NRIC in raw files of this type."
                />
                <Checkbox
                  label="Requires IC for Hashing"
                  id="dstRequiresIc"
                  name="requires_ic_for_hashing"
                  checked={currentDST.requires_ic_for_hashing}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  hintText="If checked, an IC column must be selected to generate Hashed_IDs for this data type."
                />
                <Checkbox
                  label="Perform Column Consistency Check"
                  id="dstPerformColCheck"
                  name="perform_column_consistency_check"
                  checked={currentDST.perform_column_consistency_check}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  hintText="If checked, system will try to validate if expected columns are present based on PII/Analytical defaults."
                />
                <Input
                  label="Default PII Columns to Remove (comma-separated)"
                  id="dstPiiColumns"
                  name="default_pii_columns"
                  value={currentDST.default_pii_columns}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  hintText="E.g., FullName, Address. IC column is removed automatically if hashing is required."
                />
                <Input
                  label="Default Analytical Columns to Keep (comma-separated)"
                  id="dstAnalyticalColumns"
                  name="default_analytical_columns"
                  value={currentDST.default_analytical_columns}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  hintText="E.g., ClaimAmount, Age. If empty, all non-PII/non-IC columns are kept."
                />
                <Input
                  label="Category Display Info (JSON)"
                  name="category_display_info"
                  isTextarea
                  rows={3}
                  value={currentDST.category_display_info}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  hintText='JSON object, e.g., {"icon_name": "FiTrendingUp", "instruction_text": "Upload your monthly sales data."}'
                />
                <Input
                  label="Common Aliases for Columns (JSON)"
                  name="common_aliases"
                  isTextarea
                  rows={3}
                  value={currentDST.common_aliases}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  hintText='JSON object, e.g., {"ic_number": ["NRIC", "ID Number"], "dob": ["DateOfBirth", "Birth Date"]}'
                />
                <Input
                  label="Column Guidance (JSON)"
                  name="column_guidance"
                  isTextarea
                  rows={5}
                  value={currentDST.column_guidance}
                  onChange={handleFormChange}
                  disabled={isSubmitting || isLoading}
                  hintText='JSON object, e.g., {"patient_id": {"description": "Unique patient identifier.", "format": "Alphanumeric, up to 20 chars"}, "claim_date": {"description": "Date of claim.", "format": "YYYY-MM-DD"}}'
                />
                <HStack
                  spacing="md"
                  pt="md"
                  className={adminPageStyles.formActionRow}
                >
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || isLoading}
                  >
                    {isEditMode ? 'Save Changes' : 'Create Type'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsFormVisible(false)}
                    disabled={isSubmitting || isLoading}
                  >
                    Cancel
                  </Button>
                </HStack>
              </Stack>
            </form>
          </Stack>
        ) : isLoading && dataSourceTypes.length > 0 ? (
          <Box display="flex" justifyContent="center" py="xl">
            <Spinner size="lg" />
          </Box>
        ) : dataSourceTypes.length === 0 && !isLoading && !error ? (
          <Alert variant="info">
            No Data Source Types found. Click "Create New Type" to add one.
          </Alert>
        ) : dataSourceTypes.length > 0 ? (
          <Table
            columns={columns}
            data={dataSourceTypes}
            isLoading={isLoading}
          />
        ) : null}

        {showDeleteConfirm && dstToDelete && (
          <Modal
            title={`Confirm Deletion: ${dstToDelete.display_name}`}
            isOpen={showDeleteConfirm}
            onClose={closeDeleteConfirm}
            size="md"
            footer={
              <HStack spacing="sm" justifyContent="flex-end">
                <Button
                  variant="outline"
                  onClick={closeDeleteConfirm}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  isLoading={isSubmitting}
                  disabled={
                    isSubmitting || confirmationInput !== dstToDelete.name
                  }
                >
                  Delete Type
                </Button>
              </HStack>
            }
          >
            <Stack spacing="md">
              <Typography>
                Are you sure you want to delete Data Source Type "
                <strong>{dstToDelete.display_name}</strong>"?
              </Typography>
              <Alert variant="warning" title="This action cannot be undone.">
                The backend will prevent deletion if this type is in use by a
                project. If not in use, it will be permanently deleted.
              </Alert>
              <Input
                label={
                  <span>
                    Please type the system name{' '}
                    <strong>{dstToDelete.name}</strong> to confirm.
                  </span>
                }
                id="delete-dst-confirm-input"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                autoFocus
                disabled={isSubmitting}
              />
            </Stack>
          </Modal>
        )}
      </Stack>
    </Card>
  );
}
export default DataSourceTypeManagementPage;
